import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppSelector, useAppDispatch } from '@/store';
import { extractErrorMessage } from '@/utils/common/errorHandling';
import {
  corkBoardApi,
  useCreateThreadMutation,
  useUpdateThreadMutation,
} from '@/store/api/corkBoard/corkBoardApi';
import { useCorkBoardMutation } from './useCorkBoardMutation';
import { CORK_BOARD_INPUT_LIMITS, CORK_BOARD_FILE_UPLOAD, CORK_BOARD_API, CORK_BOARD_CACHE_TAGS } from '@/constants/residents/corkBoardConstants';
import { markFeedForReplace } from '@/store/slices/residents/corkBoardUISlice';
import { SecureStorage } from '@/services/shared/storage/SecureStorage';
import { AppConfig } from '@/constants/residents/config';
import type {
  LocalAttachment,
  CorkBoardAttachment,
} from '@/types/residents/corkBoard';

// ============================================================================
// Types
// ============================================================================

export interface ThreadFormErrors {
  title?: string;
  body?: string;
  tags?: string;
  files?: string;
}

export interface ThreadFormInitialData {
  title: string;
  body: string;
  tagIds: number[];
  attachments?: CorkBoardAttachment[];
}

interface UseThreadFormOptions {
  mode: 'create' | 'edit';
  threadId?: string;
  initialData?: ThreadFormInitialData;
  onSuccess?: (threadId: string) => void;
}

// ============================================================================
// Hook
// ============================================================================

export const useThreadForm = ({ mode, threadId, initialData, onSuccess }: UseThreadFormOptions) => {
  const user = useAppSelector((state) => state.auth.user);
  const dispatch = useAppDispatch();
  const propertyId = user?.propertyId;

  // ── Form state ─────────────────────────────────────────────────────────────
  const [title, setTitle] = useState(initialData?.title ?? '');
  const [body, setBody] = useState(initialData?.body ?? '');
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>(initialData?.tagIds ?? []);
  const [attachments, setAttachments] = useState<LocalAttachment[]>([]);
  const [existingAttachments, setExistingAttachments] = useState<CorkBoardAttachment[]>(
    initialData?.attachments ?? [],
  );
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const initialDataRef = useRef(initialData);

  // Sync form state when initialData arrives asynchronously (edit mode)
  useEffect(() => {
    if (initialData && !initialDataRef.current) {
      setTitle(initialData.title);
      setBody(initialData.body);
      setSelectedTagIds(initialData.tagIds);
      setExistingAttachments(initialData.attachments ?? []);
      initialDataRef.current = initialData;
    }
  }, [initialData]);

  // ── Mutations ──────────────────────────────────────────────────────────────
  const [createThread, { isLoading: isCreating }] = useCreateThreadMutation();
  const [updateThread, { isLoading: isUpdating }] = useUpdateThreadMutation();

  const { execute: executeCreateThread } = useCorkBoardMutation(createThread, {
    errorFallback: 'Failed to create post. Please try again.',
  });
  const { execute: executeUpdateThread } = useCorkBoardMutation(updateThread, {
    errorFallback: 'Failed to update post. Please try again.',
  });

  const isSubmitting = isCreating || isUpdating || isUploading;

  // ── Validation ─────────────────────────────────────────────────────────────
  const errors = useMemo<ThreadFormErrors>(() => {
    const errs: ThreadFormErrors = {};

    if (!title.trim()) {
      errs.title = 'Title is required';
    } else if (title.trim().length > CORK_BOARD_INPUT_LIMITS.titleMax) {
      errs.title = `Title must be ${CORK_BOARD_INPUT_LIMITS.titleMax} characters or less`;
    }

    if (!body.trim()) {
      errs.body = 'Description is required';
    } else if (body.trim().length > CORK_BOARD_INPUT_LIMITS.bodyMax) {
      errs.body = `Description must be ${CORK_BOARD_INPUT_LIMITS.bodyMax} characters or less`;
    }

    if (selectedTagIds.length < CORK_BOARD_INPUT_LIMITS.minTags) {
      errs.tags = 'Select at least 1 tag';
    } else if (selectedTagIds.length > CORK_BOARD_INPUT_LIMITS.maxTags) {
      errs.tags = `Select up to ${CORK_BOARD_INPUT_LIMITS.maxTags} tags`;
    }

    const totalFiles = attachments.length + existingAttachments.length;
    if (totalFiles > CORK_BOARD_FILE_UPLOAD.maxFiles) {
      errs.files = `Maximum ${CORK_BOARD_FILE_UPLOAD.maxFiles} files allowed`;
    }

    return errs;
  }, [title, body, selectedTagIds, attachments, existingAttachments]);

  const isValid = Object.keys(errors).length === 0;

  const isDirty = useMemo(() => {
    const init = initialDataRef.current;
    if (!init) {
      return title.trim() !== '' || body.trim() !== '' || selectedTagIds.length > 0 || attachments.length > 0;
    }
    return (
      title.trim() !== init.title.trim() ||
      body.trim() !== init.body.trim() ||
      JSON.stringify([...selectedTagIds].sort()) !== JSON.stringify([...init.tagIds].sort()) ||
      attachments.length > 0 ||
      existingAttachments.length !== (init.attachments?.length ?? 0)
    );
  }, [title, body, selectedTagIds, attachments, existingAttachments]);

  // ── Actions ────────────────────────────────────────────────────────────────

  const toggleTag = useCallback((tagId: number) => {
    setSelectedTagIds((prev) => {
      if (prev.includes(tagId)) {
        return prev.filter((id) => id !== tagId);
      }
      return [tagId];
    });
  }, []);

  const addFiles = useCallback((files: LocalAttachment[]) => {
    setAttachments((prev) => {
      const remaining = CORK_BOARD_FILE_UPLOAD.maxFiles - prev.length - existingAttachments.length;
      if (remaining <= 0) return prev;
      return [...prev, ...files.slice(0, remaining)];
    });
  }, [existingAttachments.length]);

  const removeFile = useCallback((index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const removeExistingAttachment = useCallback((attachmentId: string) => {
    setExistingAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
  }, []);

  // ── XHR Upload with progress ───────────────────────────────────────────────

  const uploadWithProgress = useCallback((formData: FormData): Promise<{ id: string }> => {
    return new Promise(async (resolve, reject) => {
      const token = await SecureStorage.getInstance().getToken();
      const url = `${AppConfig.API.BASE_URL}${CORK_BOARD_API.threads.base}`;

      const xhr = new XMLHttpRequest();
      xhr.open('POST', url);

      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const pct = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(pct);
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            resolve(response?.data ?? response);
          } catch {
            reject(new Error('Invalid server response'));
          }
        } else {
          try {
            const errorBody = JSON.parse(xhr.responseText);
            const msg = errorBody?.error?.message || errorBody?.message || errorBody?.error;
            reject({ data: errorBody, message: msg || `Upload failed (${xhr.status})`, status: xhr.status });
          } catch {
            reject(new Error(
              xhr.status === 413 ? 'File is too large. Please choose a smaller file.'
              : xhr.status >= 500 ? 'Server error during upload. Please try again.'
              : `Upload failed (${xhr.status})`,
            ));
          }
        }
      };

      xhr.onerror = () => reject(new Error('Network error during upload. Please check your connection and try again.'));
      xhr.ontimeout = () => reject(new Error('Upload timed out. Please try again with a smaller file or better connection.'));
      xhr.timeout = 60000;

      xhr.send(formData);
    });
  }, []);

  // ── Submit ─────────────────────────────────────────────────────────────────

  const handleSubmit = useCallback(async () => {
    setHasAttemptedSubmit(true);
    setSubmitError(null);

    if (!isValid) return;
    if (!propertyId) {
      setSubmitError('Unable to determine property. Please try again.');
      return;
    }

    try {
      if (mode === 'create') {
        const hasFiles = attachments.length > 0;

        if (hasFiles) {
          setIsUploading(true);
          setUploadProgress(0);

          const formData = new FormData();
          formData.append('propertyId', String(propertyId));
          formData.append('title', title.trim());
          formData.append('body', body.trim());
          formData.append('tagIds', JSON.stringify(selectedTagIds));

          for (const a of attachments) {
            if (a.file) {
              formData.append('files', a.file);
            } else {
              formData.append('files', { uri: a.uri, type: a.type, name: a.name } as any);
            }
          }

          try {
            const result = await uploadWithProgress(formData);
            dispatch(markFeedForReplace());
            dispatch(corkBoardApi.util.invalidateTags([
              { type: CORK_BOARD_CACHE_TAGS.Threads, id: 'LIST' },
              { type: CORK_BOARD_CACHE_TAGS.MyThreads, id: 'LIST' },
            ]));
            if (result?.id) {
              onSuccess?.(String(result.id));
            }
          } finally {
            setIsUploading(false);
            setUploadProgress(null);
          }
        } else {
          dispatch(markFeedForReplace());
          const result = await executeCreateThread(
            { propertyId, title: title.trim(), body: body.trim(), tagIds: selectedTagIds },
            { onSuccess: (data) => { if (data?.id) onSuccess?.(data.id); } },
          );
          if (!result.success) {
            setSubmitError(result.error);
          }
        }
      } else if (mode === 'edit' && threadId) {
        const result = await executeUpdateThread(
          { threadId, title: title.trim(), body: body.trim(), tagIds: selectedTagIds },
          { onSuccess: () => onSuccess?.(threadId) },
        );
        if (!result.success) {
          setSubmitError(result.error);
        }
      }
    } catch (error: unknown) {
      const message = extractErrorMessage(error, 'Something went wrong. Please try again.');
      setSubmitError(message);
    }
  }, [
    isValid, propertyId, mode, threadId, title, body, selectedTagIds,
    attachments, executeCreateThread, executeUpdateThread, onSuccess, uploadWithProgress, dispatch,
  ]);

  // ── Return ─────────────────────────────────────────────────────────────────

  return {
    title,
    setTitle,
    body,
    setBody,
    selectedTagIds,
    toggleTag,
    attachments,
    existingAttachments,
    addFiles,
    removeFile,
    removeExistingAttachment,

    errors,
    isValid,
    isDirty,
    isSubmitting,
    uploadProgress,
    submitError,
    hasAttemptedSubmit,

    handleSubmit,
    propertyId,
  } as const;
};
